import { Box } from "@mui/material";
import { useOutletContext } from "react-router-dom";
import DeleteModal from "@/components/app/DeleteModal";
import useDisclosure from "@/hooks/use-disclosure";
import GroupAppointmentDrawer from "./components/GroupAppointmentDrawer";
import GroupAppointmentTable from "./components/GroupAppointmentTable";
import GroupRescheduleDrawer from "./components/GroupRescheduleDrawer";
import { CANCEL_SESSION_CONFIRMATION } from "./group-appointment-constant";
import { GroupAppointment } from "./group-appointment-types";
import { useGroupAppointmentList } from "./hooks/use-group-appointment-list";
import { useGroupSessionFilters } from "./hooks/use-group-session-filters";
import { useGroupSessionActions } from "./hooks/use-group-session-actions";

/**
 * Group side of the appointments list. Rendered in SchedulingLayout's outlet,
 * which owns the toolbar, date navigation and side filters.
 *
 * @author Rajkumar Rathod
 */
const GroupAppointmentList = () => {
  const appointmentDisclosure = useDisclosure();
  const {
    filterDate,
    groupProviderIds,
    groupPatientId,
    groupLocationIds,
    groupSessionStatuses,
  } = useOutletContext<{
    filterDate: [string, string];
    groupProviderIds?: string[];
    groupPatientId?: string;
    groupLocationIds: string[];
    groupSessionStatuses: string[];
  }>();

  const { groupType, therapyCategory } = useGroupSessionFilters();
  const { appointments, totalCount, isLoading, pagination, setPagination } =
    useGroupAppointmentList({
      filterDate,
      groupType,
      therapyCategory,
      providerIds: groupProviderIds,
      patientId: groupPatientId,
      locationIds: groupLocationIds,
      sessionStatuses: groupSessionStatuses,
    });

  const selectedAppointment = appointmentDisclosure.data as
    GroupAppointment | undefined;

  const actions = useGroupSessionActions(
    selectedAppointment?.uuid,
    appointmentDisclosure.close
  );

  const openAppointment = (appointment: GroupAppointment) =>
    appointmentDisclosure.open(appointment);

  return (
    <Box
      sx={{
        mr: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <GroupAppointmentTable
        appointments={appointments}
        isLoading={isLoading}
        totalCount={totalCount}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={openAppointment}
      />

      {appointmentDisclosure.opened && !!selectedAppointment && (
        <GroupAppointmentDrawer
          opened={appointmentDisclosure.opened}
          onClose={appointmentDisclosure.close}
          detail={actions.detail}
          isLoading={actions.isLoading}
          onCancel={() => actions.cancelDisclosure.open()}
          onReschedule={() => actions.rescheduleDisclosure.open()}
          canReschedule={actions.canReschedule}
          onCompleteCheckIn={actions.completeCheckIn}
          onCompleteVisit={actions.completeVisit}
          onViewSummary={actions.viewSummary}
        />
      )}

      {actions.rescheduleDisclosure.opened && !!actions.detail && (
        <GroupRescheduleDrawer
          opened={actions.rescheduleDisclosure.opened}
          onClose={actions.rescheduleDisclosure.close}
          detail={actions.detail}
          onRescheduled={actions.finishReschedule}
        />
      )}

      {actions.cancelDisclosure.opened && (
        <DeleteModal
          variant="warning"
          w="450px"
          open={actions.cancelDisclosure.opened}
          onClose={actions.cancelDisclosure.close}
          onConfirmation={actions.cancelSession}
          content={CANCEL_SESSION_CONFIRMATION.content}
          confirmButtonText={CANCEL_SESSION_CONFIRMATION.confirm}
          cancelButtonText={CANCEL_SESSION_CONFIRMATION.cancel}
        />
      )}
    </Box>
  );
};

export default GroupAppointmentList;
